import { featureCollection, point } from '@turf/helpers';
import type { CircleLayerSpecification } from 'mapbox-gl';
import { useMemo } from 'react';
import { Layer, Source } from 'react-map-gl/mapbox';
import { BORDERS_WEIGHT_MD, COLOR_BACKGROUND_TINTED, COLOR_BORDER_INTERACTIVE } from '@alltrails/denali/tokens';
import type { MarkerProperties } from '@alltrails/types/CustomRoute';
import useMap from '../../../hooks/useMap';
import { customRouteLinkableEndpointId, getLayerBeforeId } from '../../../utils/layers';
import { LINK_DISTANCE_PX } from './useAdjacentEndpointLinking';
import useLinkableEndpoint from './useLinkableEndpoint';

const SOURCE_ID = 'custom-route-linkable-endpoint-source';

// The circle covers the area a point snaps and links within
const paint: CircleLayerSpecification['paint'] = {
  'circle-color': COLOR_BACKGROUND_TINTED,
  'circle-radius': LINK_DISTANCE_PX,
  'circle-stroke-color': COLOR_BORDER_INTERACTIVE,
  'circle-stroke-width': BORDERS_WEIGHT_MD
};

type LinkableEndpointIndicatorProps = {
  dragMarker?: MarkerProperties;
  isHidden?: boolean;
};

/**
 * Marks the neighboring segment's endpoint that the next placed point, or the dragged start or end handle, links to when placed near it.
 * Ordered with the feature layers but takes no events, so clicks on it still reach the base map and place points.
 */
const LinkableEndpointIndicator = ({ dragMarker, isHidden }: LinkableEndpointIndicatorProps) => {
  const map = useMap();
  const linkableEndpoint = useLinkableEndpoint(dragMarker);
  const data = useMemo(
    () => featureCollection(linkableEndpoint && !isHidden ? [point([linkableEndpoint.endpoint[1], linkableEndpoint.endpoint[0]])] : []),
    [isHidden, linkableEndpoint]
  );

  const beforeId = getLayerBeforeId(map, customRouteLinkableEndpointId);

  return (
    <Source id={SOURCE_ID} type="geojson" data={data}>
      <Layer id={customRouteLinkableEndpointId} source={SOURCE_ID} type="circle" paint={paint} beforeId={beforeId} />
    </Source>
  );
};

export default LinkableEndpointIndicator;
